import * as React from 'react';
import { AmpProduct, useTrackAmpClicked, useTrackAmpSeen } from '@/utils/amplitude/analytics';
import { getCatalogAd } from '@/redux/modules/catalog/ad/catalogAd.selectors';
import { getCategoryIdsFromPathname } from '@/utils/urls';
import { getCategoryIdsFromQueryParams, getKeywordFromQueryParams } from '@/utils/queryParams';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import CatalogAd from './CatalogAd';
import ConsignmentAd from './ConsignmentAd';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import useVisibilitySensor from '@rooks/use-visibility-sensor';

type Props = {
    className?: string;
    isLandingPage?: boolean;
    pageName?: string;
    pathname: string;
    queryParams: any;
};

const AdContainer = ({ className = '', isLandingPage, pageName, pathname, queryParams }: Props) => {
    const dispatch = useAppDispatch();
    const targetRef = React.useRef<HTMLDivElement>(null);

    const adRef = React.useRef<HTMLDivElement>(null);
    const { isVisible } = useVisibilitySensor(adRef, {
        intervalCheck: false,
        resizeCheck: true,
        scrollCheck: true,
    });

    const trackAmpClicked = useTrackAmpClicked();
    const trackAmpSeen = useTrackAmpSeen();

    const keywords = getKeywordFromQueryParams(queryParams);
    let categoryIds = isLandingPage ? getCategoryIdsFromPathname(pathname) : getCategoryIdsFromQueryParams(queryParams);
    categoryIds = pageName === 'calendar' ? '' : categoryIds;

    const adUnit = useAppSelector((state) => getCatalogAd(state, { categoryIds, keywords: `${keywords}`, pageName }));
    const previousIsVisible = usePrevious(isVisible);
    const [visibilityMessageSent, setVisibilityMessageSent] = React.useState(false);
    const { adUnitId, catalogId, consignment, sellerId } = adUnit;

    React.useEffect(() => {
        if (!previousIsVisible && isVisible && !visibilityMessageSent && Boolean(adUnitId)) {
            // @ts-ignore
            dispatch(
                trackAdUnitVisible({
                    adUnitId,
                    page: pageName,
                })
            ).then(() => {
                setVisibilityMessageSent(true);
            });

            // Track AMP Seen event
            trackAmpSeen({ ampProduct: AmpProduct.CategoryPageBanner, catalogId, houseId: sellerId });
        }
    }, [
        dispatch,
        isVisible,
        adUnitId,
        previousIsVisible,
        catalogId,
        consignment,
        pageName,
        sellerId,
        visibilityMessageSent,
        trackAmpSeen,
    ]);

    React.useEffect(() => {
        setVisibilityMessageSent(false);
    }, [adUnitId]);

    const trackClick = React.useCallback(() => {
        dispatch(
            trackAdUnitClick({
                adUnitId,
                consignment,
                page: pageName || '',
            })
        );

        // Track AMP Clicked event
        trackAmpClicked({ ampProduct: AmpProduct.CategoryPageBanner, catalogId, houseId: sellerId });
    }, [dispatch, adUnitId, catalogId, consignment, pageName, sellerId, trackAmpClicked]);

    const [componentOffset, setComponentOffset] = React.useState(0);
    const [windowHeight, setWindowHeight] = React.useState(0);
    const [componentHeight, setComponentHeight] = React.useState(0);
    const [scrolled, setScrolled] = React.useState(false);

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const newWindowHeight = document?.documentElement?.scrollHeight;
        if (windowHeight !== newWindowHeight) {
            setWindowHeight(newWindowHeight || 1500);
        }
        const newComponentHeight = targetRef?.current?.clientHeight;
        if (componentHeight !== newComponentHeight && newComponentHeight) {
            setComponentHeight(newComponentHeight);
        }
    }

    const noAd = !adUnit || !adUnit.adUnitId;

    useScrollPosition(
        () => {
            if (!scrolled) {
                setScrolled(true);
            }
            setComponentOffset(targetRef?.current?.getBoundingClientRect()?.top || 100);
        },
        [scrolled],
        noAd ? undefined : targetRef,
        false,
        10
    );

    if (noAd) {
        return null;
    }

    const componentParent = targetRef.current?.parentElement;
    const componentParentBottom = componentParent?.getBoundingClientRect()?.bottom;
    const componentBottomGap = componentParentBottom - componentHeight;
    const componentParentChildrenTotalHeight = Array.from(componentParent?.children || []).reduce((acc, child) => {
        const element = child as HTMLElement;
        const computedStyle = window.getComputedStyle(element);
        const marginTop = parseInt(computedStyle.marginTop, 10);
        const marginBottom = parseInt(computedStyle.marginBottom, 10);
        const height = element.getBoundingClientRect().height + marginTop + marginBottom;
        return acc + height;
    }, 0);

    const tooSmallToMove = componentParentChildrenTotalHeight >= componentParent?.clientHeight;
    const stickBottom = !tooSmallToMove && Boolean(componentBottomGap <= 0);
    const stick = !tooSmallToMove && Boolean(componentOffset <= 0);

    const Ad = consignment ? ConsignmentAd : CatalogAd;

    return (
        <StyledAdContainer
            data-testid="ad-container"
            ref={targetRef}
        >
            <Ad
                adUnit={{ ...adUnit, sellerId }}
                className={className}
                scrolled={scrolled}
                stick={stick}
                stickBottom={stickBottom}
                trackClick={trackClick}
            >
                <StyledVisibilityElement ref={adRef} />
            </Ad>
        </StyledAdContainer>
    );
};

export default React.memo<Props>(AdContainer, isEqual);

const StyledAdContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledVisibilityElement = styled.div`
    height: 1px;
    width: 100%;
    background-color: transparent;
`;
